import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

/**
 * Generic resource drawer form.
 * fields: [{ name, label, type: 'text' | 'number' | 'select' | 'date' | 'switch' | 'textarea', options?, placeholder?, span? }]
 */
export default function ResourceDrawer({ open, onClose, title, fields, initial = {}, onSave, saveLabel = "Save" }) {
    const [form, setForm] = useState({});
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) setForm({ ...initial });
    }, [open]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await onSave(form);
            toast.success("Saved");
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to save");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
            <SheetContent side="right" className="w-full sm:max-w-[520px] p-0 flex flex-col" data-testid="resource-drawer">
                <SheetHeader className="border-b border-border px-6 py-4">
                    <SheetTitle className="font-serif text-2xl font-light">{title}</SheetTitle>
                </SheetHeader>
                <form onSubmit={submit} className="flex-1 overflow-y-auto p-6 grid grid-cols-2 gap-4">
                    {fields.map((f) => (
                        <div key={f.name} className={f.span === 2 ? "col-span-2" : "col-span-1"}>
                            <Label className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">{f.label}</Label>
                            {f.type === "select" ? (
                                <Select value={form[f.name]?.toString() || ""} onValueChange={(v) => set(f.name, v)}>
                                    <SelectTrigger className="mt-1.5" data-testid={`field-${f.name}`}><SelectValue placeholder={f.placeholder || "Select..."} /></SelectTrigger>
                                    <SelectContent>
                                        {(f.options || []).map((o) => (
                                            <SelectItem key={o.value} value={o.value.toString()}>{o.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            ) : f.type === "switch" ? (
                                <div className="mt-2"><Switch checked={!!form[f.name]} onCheckedChange={(v) => set(f.name, v)} data-testid={`field-${f.name}`} /></div>
                            ) : f.type === "textarea" ? (
                                <textarea className="mt-1.5 w-full h-24 px-3 py-2 bg-muted/40 border border-border rounded-md text-[13px]" value={form[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} data-testid={`field-${f.name}`} />
                            ) : (
                                <Input
                                    type={f.type || "text"}
                                    value={form[f.name] ?? ""}
                                    onChange={(e) => set(f.name, f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
                                    placeholder={f.placeholder}
                                    className="mt-1.5"
                                    data-testid={`field-${f.name}`}
                                />
                            )}
                        </div>
                    ))}
                    <div className="col-span-2 flex items-center gap-2 pt-2 border-t border-border mt-2">
                        <Button type="submit" disabled={busy} data-testid="drawer-save">{busy ? "Saving…" : saveLabel}</Button>
                        <Button type="button" variant="outline" onClick={onClose} data-testid="drawer-cancel">Cancel</Button>
                    </div>
                </form>
            </SheetContent>
        </Sheet>
    );
}
